import { useCallback, useEffect, useState } from "react";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth, useOutletApi } from "@/features/authentication";
import {
  GeneralInformationSection,
  OtherPaymentDetailsSection,
} from "@/features/other-payments/other-payment";
import { PermissionEnum } from "@/features/permissions";
import { getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  CustomDialogTitle,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  useSnackbar,
} from "@/features/ui";

export const OtherPayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission } = useAuth();
  const outletApi = useOutletApi();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [otherPayment, setOtherPayment] = useState(null);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadOtherPayment = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await outletApi.get(`other-payments/${referenceNumber}`);
      setOtherPayment(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat pembayaran"));
    } finally {
      setLoading(false);
    }
  }, [outlet, outletApi, referenceNumber]);

  useEffect(() => {
    loadOtherPayment();
  }, [loadOtherPayment]);

  const handleDelete = async () => {
    if (!otherPayment || !outlet) return;

    try {
      setIsDeleting(true);
      await outletApi.delete(`other-payments/${referenceNumber}`, {
        headers: { "Outlet-Id": outlet.id },
      });
      showSnackbar("Pembayaran berhasil dihapus", "success");
      navigate("/general-ledger/other-payments");
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menghapus pembayaran"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // A payment generated from a POS expense is owned by that expense; the
  // backend rejects editing or deleting it through these endpoints.
  const isSystemManaged = Boolean(otherPayment?.expenseId);

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.DELETE_OTHER_PAYMENTS) &&
        !isSystemManaged && (
          <ResponsiveToolbarButton
            label="Hapus"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          />
        )}
      {hasPermission(PermissionEnum.UPDATE_OTHER_PAYMENTS) &&
        !isSystemManaged && (
          <ResponsiveToolbarButton
            label="Ubah"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate("edit")}
          />
        )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
    </>
  );

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Pembayaran"
      breadcrumbs={[
        { title: "Pembayaran", path: "/general-ledger/other-payments" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection otherPayment={otherPayment} />
        <OtherPaymentDetailsSection
          otherPaymentDetails={otherPayment.otherPaymentDetails || []}
        />

        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="OTHER_PAYMENT"
          transactionableId={otherPayment?.id}
        />

        {deleteDialogOpen && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Pembayaran"
              onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus pembayaran{" "}
                <strong>{referenceNumber}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
